'use client';

import { useContext, useMemo, type FC } from 'react';
import { Button, ButtonGroup, Flex, Text, Icon } from '@/_components';
import { ClientQuestionnaireContext } from '@/_contexts/client/ClientQuestionnaireProvider';
import styles from './QuestionnaireNextSectionHeader.module.css';
import type { QuestionnaireNextSectionHeaderProps } from './QuestionnaireNextSectionHeader.types';
import { PreviewContext } from '@/_contexts/PreviewProvider';

export const QuestionnaireNextSectionHeader: FC<
  QuestionnaireNextSectionHeaderProps
> = ({ mode }) => {
  const {
    isQuestionnairePending,
    goToNextIncompleteSection,
    setShowIncompleteNavigation,
    showIncompleteNavigation,
  } = useContext(ClientQuestionnaireContext);

  const {
    isPreviewQuestionnairePending,
    showIncompletePreviewNavigation,
    goToNextIncompletePreviewSection,
    setShowIncompletePreviewNavigation,
  } = useContext(PreviewContext);

  const isPending: boolean = useMemo(() => {
    if (mode === 'admin-preview') return isPreviewQuestionnairePending;
    if (mode === 'admin-template') return false;

    return isQuestionnairePending;
  }, [isQuestionnairePending, isPreviewQuestionnairePending, mode]);

  const isShowingNavigation: boolean = useMemo(() => {
    if (mode === 'admin-preview') return showIncompletePreviewNavigation;
    if (mode === 'admin-template') return false;

    return showIncompleteNavigation;
  }, [showIncompleteNavigation, showIncompletePreviewNavigation, mode]);

  if (!isPending || !isShowingNavigation) return null;

  const onClose = (): void => {
    if (mode === 'admin-preview') {
      return setShowIncompletePreviewNavigation(false);
    }
    if (mode === 'admin-template') return;

    return setShowIncompleteNavigation(false);
  };

  const onGotoNext = (): void => {
    if (mode === 'admin-preview') return goToNextIncompletePreviewSection();

    return goToNextIncompleteSection();
  };

  return (
    <div className={styles.Container}>
      <Flex
        className={styles.FixedContainer}
        gap={6}
        padding={8}
        isYCentered={true}
        isBetween={true}
      >
        <Flex isYCentered={true} gap={4}>
          <Icon name="x-circle" size={20} color="currentcolor" />
          <Text color="primary-on-color">
            Almost there! Please complete the missing sections before
            submitting.
          </Text>
        </Flex>
        <ButtonGroup align="right" isSameLine={true}>
          <Button
            intent="white-fill"
            onClick={onGotoNext}
            iconRight="arrow-right"
            value="Next Incomplete Section"
          />
          <Button
            value="Close"
            isIconOnly={true}
            size="small"
            iconLeft="x"
            intent="white"
            onClick={onClose}
          />
        </ButtonGroup>
      </Flex>
    </div>
  );
};
